import React, { useState, useEffect } from "react";
import { ref, onValue, remove, update } from "firebase/database";
import { database } from "../../../firebaseConfig";
import { Package, Clock, CheckCircle, XCircle, Trash2, Calendar, Download, Filter, Eye, X } from "lucide-react";
import toast from "react-hot-toast";

const RESPONSE_STATUSES = ["seen", "rejected"];

const ClientOrdersTab = ({ companyName }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { orderId, clientId, order }
  const [deleting, setDeleting] = useState(false);
  const [dateFilter, setDateFilter] = useState("all"); // all, today, week, month, custom
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState("seen");
  const [responseNote, setResponseNote] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (!companyName) return;

    const ordersRef = ref(database, `Companies/${companyName}/ClientOrders`);

    const unsubscribe = onValue(ordersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const ordersList = [];

        // Extract all orders from all clients
        Object.entries(data).forEach(([clientId, clientData]) => {
          if (clientData.orders) {
            Object.entries(clientData.orders).forEach(([orderId, order]) => {
              ordersList.push({
                orderId,
                clientId,
                ...order,
              });
            });
          }
        });

        // Sort by date (newest first)
        ordersList.sort((a, b) => new Date(b.orderDate || 0) - new Date(a.orderDate || 0));
        setOrders(ordersList);
      } else {
        setOrders([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyName]);

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "seen":
        return <Eye className="text-blue-600" size={20} />;
      case "rejected":
        return <XCircle className="text-red-600" size={20} />;
      case "completed":
        return <CheckCircle className="text-green-600" size={20} />;
      case "pending":
        return <Clock className="text-yellow-600" size={20} />;
      case "cancelled":
        return <XCircle className="text-red-600" size={20} />;
      default:
        return <Package className="text-gray-600" size={20} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "seen":
        return "text-blue-600 bg-blue-50";
      case "rejected":
        return "text-red-600 bg-red-50";
      case "completed":
        return "text-green-600 bg-green-50";
      case "pending":
        return "text-yellow-600 bg-yellow-50";
      case "cancelled":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (filter === "All") return true;
    return order.status?.toLowerCase() === filter.toLowerCase();
  });

  // Apply date filtering
  const dateFilteredOrders = filteredOrders.filter((order) => {
    if (dateFilter === "all") return true;
    
    const orderDate = new Date(order.orderDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (dateFilter) {
      case "today":
        const orderDay = new Date(orderDate);
        orderDay.setHours(0, 0, 0, 0);
        return orderDay.getTime() === today.getTime();
      
      case "week":
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return orderDate >= weekAgo;
      
      case "month":
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return orderDate >= monthAgo;
      
      case "custom":
        if (!customStartDate && !customEndDate) return true;
        const start = customStartDate ? new Date(customStartDate) : new Date(0);
        const end = customEndDate ? new Date(customEndDate) : new Date();
        end.setHours(23, 59, 59, 999);
        return orderDate >= start && orderDate <= end;
      
      default:
        return true;
    }
  });

  // Returns total liters for an order.
  // If order.items is provided, it is expected to be an array of objects with a numeric `quantity`
  // representing liters per line item. Otherwise, the top-level `quantity` is treated as total liters.
  const getOrderLiters = (order) => {
    if (Array.isArray(order.items) && order.items.length > 0) {
      return order.items.reduce(
        (sum, item) => sum + (Number(item.quantity) || 0),
        0
      );
    }
    if (order.quantity) return Number(order.quantity) || 0;
    return 0;
  };

  // Calculate summary stats from date-filtered orders
  const stats = {
    total: dateFilteredOrders.length,
    pending: dateFilteredOrders.filter((o) => o.status?.toLowerCase() === "pending").length,
    completed: dateFilteredOrders.filter((o) => o.status?.toLowerCase() === "completed").length,
    cancelled: dateFilteredOrders.filter((o) => o.status?.toLowerCase() === "cancelled").length,
    totalRevenue: dateFilteredOrders.reduce((sum, o) => sum + (Number(o.totalAmount) || 0), 0),
    totalLiters: dateFilteredOrders.reduce((sum, o) => sum + getOrderLiters(o), 0),
  };

  // Delete order handler
  const handleDeleteOrder = async () => {
    if (!deleteConfirm) return;

    setDeleting(true);
    try {
      const orderRef = ref(
        database,
        `Companies/${companyName}/ClientOrders/${deleteConfirm.clientId}/orders/${deleteConfirm.orderId}`
      );
      
      await remove(orderRef);
      toast.success("Order deleted successfully");
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error("Failed to delete order. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // Export orders to CSV
  const exportToCSV = () => {
    if (dateFilteredOrders.length === 0) {
      toast.error("No orders to export");
      return;
    }

    const headers = ["Order ID", "Client", "Date", "Quantity", "Total Amount", "Status", "Delivery Address"];
    const rows = dateFilteredOrders.map(order => [
      order.orderId || "N/A",
      order.clientName || order.clientId || "Unknown",
      order.orderDate ? new Date(order.orderDate).toLocaleDateString() : "N/A",
      order.quantity ? `${order.quantity} liters` : (Array.isArray(order.items) ? `${order.items.length} item(s)` : "N/A"),
      (order.totalAmount || 0).toLocaleString(),
      order.status || "N/A",
      order.deliveryAddress || "N/A"
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `orders_${dateFilter}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success("Orders exported successfully");
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    const normalizedStatus = order.status?.toLowerCase();
    const responseStatus = RESPONSE_STATUSES.includes(normalizedStatus)
      ? normalizedStatus
      : RESPONSE_STATUSES[0];
    setStatusUpdate(responseStatus);
    setResponseNote(order.companyResponseNote || "");
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder) return;
    if (!RESPONSE_STATUSES.includes(statusUpdate)) {
      toast.error(`Please select '${RESPONSE_STATUSES.join("' or '")}' before updating.`);
      return;
    }
    setUpdatingStatus(true);
    try {
      const orderRef = ref(
        database,
        `Companies/${companyName}/ClientOrders/${selectedOrder.clientId}/orders/${selectedOrder.orderId}`
      );

      await update(orderRef, {
        status: statusUpdate,
        companyResponseNote: responseNote,
        statusUpdatedAt: new Date().toISOString(),
      });

      toast.success(`Order marked as ${statusUpdate}`);
      setSelectedOrder(null);
      setResponseNote("");
    } catch (error) {
      console.error("Error updating order status:", error);
      toast.error("Failed to update order status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <p className="text-gray-600">Loading orders...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Date Filter Section */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <Filter className="w-5 h-5 text-cyan-700" />
          <h3 className="text-lg font-semibold text-cyan-700">Filter by Date</h3>
        </div>
        
        <div className="flex flex-wrap gap-2 mb-3">
          <button
            onClick={() => { setDateFilter("all"); setShowDatePicker(false); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              dateFilter === "all"
                ? "bg-cyan-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            All Time
          </button>
          <button
            onClick={() => { setDateFilter("today"); setShowDatePicker(false); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              dateFilter === "today"
                ? "bg-cyan-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Today
          </button>
          <button
            onClick={() => { setDateFilter("week"); setShowDatePicker(false); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              dateFilter === "week"
                ? "bg-cyan-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Last 7 Days
          </button>
          <button
            onClick={() => { setDateFilter("month"); setShowDatePicker(false); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              dateFilter === "month"
                ? "bg-cyan-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Last 30 Days
          </button>
          <button
            onClick={() => { setDateFilter("custom"); setShowDatePicker(!showDatePicker); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
              dateFilter === "custom"
                ? "bg-cyan-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <Calendar className="w-4 h-4" />
            Custom Range
          </button>
          {dateFilteredOrders.length > 0 && (
            <button
              onClick={exportToCSV}
              className="ml-auto px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          )}
        </div>

        {/* Custom Date Range Picker */}
        {showDatePicker && dateFilter === "custom" && (
          <div className="flex flex-wrap gap-3 p-3 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>
        )}

        {/* Date Range Info */}
        {dateFilter !== "all" && (
          <div className="mt-3 text-sm text-gray-600">
            {dateFilter === "today" && `Showing orders from today (${new Date().toLocaleDateString()})`}
            {dateFilter === "week" && "Showing orders from the last 7 days"}
            {dateFilter === "month" && "Showing orders from the last 30 days"}
            {dateFilter === "custom" && customStartDate && customEndDate && 
              `Showing orders from ${new Date(customStartDate).toLocaleDateString()} to ${new Date(customEndDate).toLocaleDateString()}`}
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Orders</p>
          <p className="text-2xl font-bold text-cyan-700">{stats.total}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Completed</p>
          <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Cancelled</p>
          <p className="text-2xl font-bold text-red-600">{stats.cancelled}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
          <p className="text-xl font-bold text-cyan-700">KES {stats.totalRevenue.toLocaleString()}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Volume (Liters)</p>
          <p className="text-2xl font-bold text-cyan-700">{stats.totalLiters.toFixed(1)}</p>
          <p className="text-xs text-gray-500 mt-1">Based on selected date range</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b">
        {["All", "Pending", "Completed", "Cancelled"].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`pb-2 px-4 font-medium transition-all ${
              filter === status
                ? "text-cyan-700 border-b-2 border-cyan-700"
                : "text-gray-500 hover:text-cyan-600"
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Orders List */}
      {dateFilteredOrders.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No {filter !== "All" ? filter.toLowerCase() : ""} orders found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-cyan-600 text-white">
              <tr>
                <th className="text-left py-3 px-4">Order ID</th>
                <th className="text-left py-3 px-4">Client</th>
                <th className="text-left py-3 px-4">Date</th>
                <th className="text-left py-3 px-4">Items</th>
                <th className="text-left py-3 px-4">Total Amount</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-center py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dateFilteredOrders.map((order, index) => (
                <tr
                  key={order.orderId}
                  className={`border-t hover:bg-cyan-50 transition ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  <td className="py-3 px-4 font-medium text-gray-800">
                    {order.orderId?.substring(0, 8) || "N/A"}
                  </td>
                  <td className="py-3 px-4 text-gray-700">
                    {order.clientName || order.clientId || "Unknown"}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {order.orderDate
                      ? new Date(order.orderDate).toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {Array.isArray(order.items)
                      ? `${order.items.length} item(s)`
                      : order.quantity
                      ? `${order.quantity} liters`
                      : "N/A"}
                  </td>
                  <td className="py-3 px-4 font-semibold text-gray-700">
                    KES {(order.totalAmount || 0).toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                        order.status
                      )}`}
                    >
                      {getStatusIcon(order.status)}
                      {order.status || "N/A"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button 
                      className="bg-cyan-600 text-white px-3 py-1 rounded-md hover:bg-cyan-700 text-sm transition"
                      title="View Details"
                      onClick={() => handleViewOrder(order)}
                    >
                      View
                    </button>
                      <button
                        onClick={() => setDeleteConfirm({ orderId: order.orderId, clientId: order.clientId, order })}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition"
                        title="Delete order"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* View / Respond Modal */}
      {selectedOrder && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => !updatingStatus && setSelectedOrder(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-xl w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-cyan-100 rounded-full">
                  <Eye className="w-6 h-6 text-cyan-700" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Order details</h3>
                  <p className="text-sm text-gray-600">Review and update client order status</p>
                </div>
              </div>
              <button
                onClick={() => !updatingStatus && setSelectedOrder(null)}
                className="text-gray-400 hover:text-gray-600"
                title="Close"
                disabled={updatingStatus}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 mb-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Client</p>
                  <p className="font-semibold text-gray-800">
                    {selectedOrder.clientName || selectedOrder.clientId || "Unknown"}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Requested Milk</p>
                  <p className="font-semibold text-gray-800">
                    {`${getOrderLiters(selectedOrder).toFixed(1)} liters`}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Delivery Date</p>
                  <p className="font-semibold text-gray-800">
                    {selectedOrder.deliveryDate
                      ? new Date(selectedOrder.deliveryDate).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Current Status</p>
                  <p className="font-semibold text-gray-800 capitalize">
                    {selectedOrder.status || "pending"}
                  </p>
                </div>
              </div>
              {selectedOrder.deliveryAddress && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Delivery Address</p>
                  <p className="text-sm text-gray-700">{selectedOrder.deliveryAddress}</p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Update status
                </label>
                <select
                  value={statusUpdate}
                  onChange={(e) => setStatusUpdate(e.target.value)}
                  disabled={updatingStatus}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="seen">Mark as Seen</option>
                  <option value="rejected">Reject Order</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message to client (optional)
                </label>
                <textarea
                  value={responseNote}
                  onChange={(e) => setResponseNote(e.target.value)}
                  disabled={updatingStatus}
                  rows="3"
                  placeholder="Share details or reasons for rejection"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => !updatingStatus && setSelectedOrder(null)}
                disabled={updatingStatus}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Close
              </button>
              <button
                onClick={handleUpdateStatus}
                disabled={updatingStatus}
                className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {updatingStatus ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Send Response
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => !deleting && setDeleteConfirm(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Delete Order</h3>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 mb-2">
                <strong>Order ID:</strong> {deleteConfirm.orderId}
              </p>
              <p className="text-sm text-red-800 mb-2">
                <strong>Client:</strong> {deleteConfirm.order.clientName || deleteConfirm.clientId}
              </p>
              <p className="text-sm text-red-800">
                <strong>Amount:</strong> KES {Number(deleteConfirm.order.totalAmount || 0).toLocaleString()}
              </p>
            </div>

            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this order? This will permanently remove the order from your records. 
              This is typically done after the order has been delivered and you want to keep track of recent orders only.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteOrder}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete Order
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientOrdersTab;
